import {ReactNode, useContext, useEffect, useState} from "react";
import {FormContext} from "@/components/form/Form.tsx";
import {nanoid} from "@reduxjs/toolkit";


export default function FieldArray(
    {name, children, defaultValue,end}:
    { name: string, children: (iter: FieldArrayActions[]) => ReactNode[], defaultValue?: any, end?:(actions: {add: (val: any)=>void}, array: FormContextType[])=> ReactNode }
) {
    const form = useContext(FormContext);
    useEffect(() => {
        if (form.getValue(name) === undefined) {
            if (defaultValue) {
                form.setValue(name, defaultValue);
                setUUIDs(defaultValue.map(() => nanoid()));
            } else {
                form.setValue(name, []);
            }
            form.setTouchedField(name, [{}]);
            form.setError(name, [{}]);
        }
    }, [defaultValue, form]);

    const [uuids, setUUIDs] = useState<string[]>([]);

    function createContexts(): FormContextType[] {
        if (Array.isArray(form.getValue(name))) {
            return form.getValue(name).map((value: any, index: number) => {
                return {
                    getValue: (n: string) => {
                        return value[n];
                    },
                    setValue: (n: string, val: any) => {
                        form.setValue(name, form.getValue(name).map((v: any, i: number) => {
                            if (i === index) {
                                return {...v, [n]: val}
                            }
                            return v;
                        }))
                    },
                    setValues: (val: any) => {
                        form.setValue(name, form.getValue(name).map((v: any, i: number) => {
                            if (i === index) {
                                return {...v, ...val}
                            }
                            return v;
                        }))
                    },
                    getError: (n: string) => {
                        return form.getError(name)?.[index]?.[n];
                    },
                    setError: (n: string, e: any) => {
                        if (form.getError(name) === undefined || form.getError(name)?.length === 0) {
                            const error = form.getValue(name).map(() => ({}));
                            error[index][n] = e;
                            form.setError(name, error);
                        } else {
                            form.setError(name, form.getError(name).map((v: any, i: number) => {
                                if (i === index) {
                                    return {...v, [n]: e}
                                }
                                return v;
                            }))
                        }
                    },
                    touchField: (n: string) => {
                        if (form.getTouchedField(name) == undefined || form.getTouchedField(name)?.length === 0 || !Array.isArray(form.getTouchedField(name)) ) {
                            const touched = form.getValue(name).map(() => ({}));
                            touched[index][n] = true;
                            form.setTouchedField(name, touched);
                        } else {
                            form.setTouchedField(name, form.getTouchedField(name).map((v: any, i: number) => {
                                if (i === index) {
                                    return {...v, [n]: true}
                                }
                                return v;
                            }))
                        }
                    },
                    setTouchedField: (n: string, val: any) => {
                        if(form.getTouchedField(name)?.length === 0 || form.getTouchedField(name) === undefined) {
                            const touched = form.getValue(name).map(() => ({}));
                            touched[index][n] = val;
                            form.setTouchedField(name, touched);
                        } else {
                            form.setTouchedField(name, form.getTouchedField(name).map((v: any, i: number) => {
                                if (i === index) {
                                    return {...v, [n]: val}
                                }
                                return v;
                            }))
                        }
                    },
                    getTouchedField: (n: string) => {
                        if(form.getTouchedField(name) === undefined || form.getTouchedField(name)?.length === 0) {
                            const touched = form.getValue(name).map(() => ({}));
                            form.setTouchedField(name, touched);
                            return touched[index][n];
                        }
                        return form.getTouchedField(name)?.[index]?.[n] ?? {};
                    },
                    submit: async () => {
                        await form.submit();
                    }
                }
            })
        } else {
            return [];
        }
    }

    function add(value?: any) {
        form.setValue(name, [...form.getValue(name), value ?? {}]);
        form.setError(name, [...(form.getError(name) ?? []), {}]);
        form.setTouchedField(name, [...(form.getTouchedField(name) ?? []), {}]);
        setUUIDs([...uuids, nanoid()]);
    }

    const childrenArray = children(createContexts().map((_, index): FieldArrayActions => {
        if (Array.isArray(form.getValue(name))) {

            return {
                getRootForm(): FormContextType | undefined {
                    return form.getRootForm();
                },
                getErrors(): any {
                    return form.getError(name)?.[index];
                },
                getValues(): any {
                    return form.getValue(name)[index];
                },
                add,
                remove(index: number) {
                    form.setValue(name, form.getValue(name).toSpliced(index, 1));
                    form.setError(name, form.getError(name).toSpliced(index, 1));
                    form.setTouchedField(name, form.getTouchedField(name).toSpliced(index, 1));
                    // @ts-ignore  it exists since 2023, but only in ecmasrcipt 2025 standard, and ts does not know that yet
                    setUUIDs(uuids.toSpliced(index, 1));
                },
                key: uuids[index]
            }
        } else {
            return {
                getRootForm() {
                    return undefined;
                },
                getErrors(): any {
                },
                getValues(): any {
                },
                add() {
                },
                remove() {
                },
                key: ''
            }
        }
    }));


    return (
        <>
            {
                createContexts().map((context, index) => {
                    return (
                        <FormContext.Provider value={context} key={index}>
                            {childrenArray[index]}
                        </FormContext.Provider>
                    )
                })
            }
            {end && end({add}, createContexts())}
        </>
    )
}